import { orderStatusConstants } from "constants/order";
import styled from "styled-components";

export type StatusBadgeTone =
  | "positive"
  | "attention"
  | "info"
  | "neutral"
  | "muted"
  | "negative";

// Badge tone per order status. Drafts are the ones that need action, so they
// take the attention tone; finished-but-inactive statuses are muted.
export const STATUS_BADGE_TONES: Record<string, StatusBadgeTone> = {
  [orderStatusConstants.PAID]: "positive",
  [orderStatusConstants.PARTIALLY_PAID]: "positive",
  [orderStatusConstants.PAID_VIA_CASH_UNMANAGED]: "positive",
  [orderStatusConstants.NO_BALANCE_DUE]: "muted",
  [orderStatusConstants.DRAFT]: "attention",
  [orderStatusConstants.SENT_TO_PATIENT]: "info",
  [orderStatusConstants.EXPIRED]: "muted",
  [orderStatusConstants.CANCELED]: "muted",
  [orderStatusConstants.REFUNDED]: "muted",
  [orderStatusConstants.DELETED]: "muted",
};

// Icons are per status rather than per tone: "Expired" and "No Balance Due"
// share the muted tone but read very differently.
export const STATUS_BADGE_ICONS: Record<string, string> = {
  [orderStatusConstants.PAID]: "pi pi-check-circle",
  [orderStatusConstants.PARTIALLY_PAID]: "pi pi-check-circle",
  [orderStatusConstants.PAID_VIA_CASH_UNMANAGED]: "pi pi-check-circle",
  [orderStatusConstants.NO_BALANCE_DUE]: "pi pi-check-circle",
  [orderStatusConstants.DRAFT]: "pi pi-exclamation-triangle",
  [orderStatusConstants.SENT_TO_PATIENT]: "pi pi-send",
  [orderStatusConstants.EXPIRED]: "pi pi-clock",
  [orderStatusConstants.CANCELED]: "pi pi-times-circle",
  [orderStatusConstants.REFUNDED]: "pi pi-replay",
  [orderStatusConstants.DELETED]: "pi pi-trash",
};

// Each tone is one colour: the text takes it at full strength, the fill and
// border are tints of it, so a new tone needs a single value.
const TONE_COLORS: Record<StatusBadgeTone, string> = {
  positive: "var(--chip-text-success)",
  attention: "var(--ping-text-color-warning)",
  info: "var(--brand-purple-700)",
  neutral: "var(--brand-gray-700)",
  muted: "var(--brand-gray-600)",
  negative: "var(--red-700)",
};

const BadgeStyled = styled.span<{ $tone: StatusBadgeTone }>`
  padding: 0.375rem 0.75rem;

  border-radius: 999px;
  background-color: ${({ $tone }) =>
    `color-mix(in srgb, ${TONE_COLORS[$tone]} 12%, var(--brand-white))`};
  border: 1px solid
    ${({ $tone }) =>
      `color-mix(in srgb, ${TONE_COLORS[$tone]} 35%, var(--brand-white))`};

  color: ${({ $tone }) => TONE_COLORS[$tone]};
  font-size: 0.8125rem;
  font-weight: var(--font-weight-medium);
  white-space: nowrap;

  .p-badge-icon {
    font-size: 0.875rem;
  }
`;

export function StatusBadge({
  label,
  tone,
  icon,
}: {
  label: string;
  tone: StatusBadgeTone;
  icon?: string;
}) {
  return (
    <BadgeStyled $tone={tone} className="inline-flex align-items-center gap-2">
      {!!icon && <i className={`${icon} p-badge-icon`} />}
      {label}
    </BadgeStyled>
  );
}
